import type { ReactNode } from "react";
import { useCallback, useMemo, useRef } from "react";

import type { ScrollGroupItem } from "./context";
import { ScrollGroupContext } from "./context";

/**
 * Provider for useScrollGroupItem() and useScrollGroupTo() functionality.
 * Unifies and syncs scrolling for all scrollGroupItems added with useScrollGroupItem()
 *
 * Inspired by
 * https://stackoverflow.com/a/70035102/14438753
 * https://stackoverflow.com/questions/56164315/how-to-describe-type-scroll-events
 */
export function ScrollGroup({
  children,
  onScrollItem,
  onAddItem,
  onResizeItem,
}: {
  children: ReactNode;
  onScrollItem?: (element: HTMLElement, event: Event) => void;
  onAddItem?: (element: HTMLElement) => void;
  onResizeItem?: (element: HTMLElement) => void;
}) {
  const scrollGroupItemsRef = useRef<ScrollGroupItem[]>([]);
  const currentManualScrollMetaRef = useRef(null);

  const registerItem = useCallback((item: ScrollGroupItem) => {
    scrollGroupItemsRef.current.push(item);
    return () => {
      scrollGroupItemsRef.current = scrollGroupItemsRef.current.filter(
        (existingItem) => existingItem !== item,
      );
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      registerItem,
      scrollGroupItemsRef,
      currentManualScrollMetaRef,
      onScrollItem,
      onAddItem,
      onResizeItem,
    }),
    [onScrollItem, registerItem, onAddItem, onResizeItem],
  );

  return (
    <ScrollGroupContext.Provider value={contextValue}>
      {children}
    </ScrollGroupContext.Provider>
  );
}
